<template>
  <div class="empresas-lista">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Lista de Empresas</h1>
        <p class="content-header-subtitle">Gestiona todas las empresas del sistema</p>
      </div>
      <div class="content-header-right">
        <b-button variant="primary" @click="nuevaEmpresa">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          Nueva Empresa
        </b-button>
      </div>
    </div>

    <!-- Filtros -->
    <b-card class="mb-3">
      <b-row>
        <b-col md="3">
          <b-form-group label="Buscar">
            <b-input-group>
              <b-form-input
                v-model="filtros.busqueda"
                placeholder="Buscar empresas..."
                @input="filtrarEmpresas"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary">
                  <feather-icon icon="SearchIcon" size="16" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Tipo de Empresa">
            <b-form-select
              v-model="filtros.tipoEmpresa"
              :options="opcionesTipoEmpresa"
              @change="filtrarEmpresas"
            >
              <template #first>
                <b-form-select-option :value="null">Todos los tipos</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Buscar por">
            <b-form-select
              v-model="filtros.tipoBusqueda"
              :options="opcionesTipoBusqueda"
            />
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-button variant="outline-secondary" @click="limpiarFiltros">
            Limpiar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabla de Empresas -->
    <b-card>
      <b-table
        :items="empresasFiltradas"
        :fields="empresasFields"
        responsive
        striped
        hover
        :busy="cargando"
        show-empty
        empty-text="No se encontraron empresas"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong> Cargando...</strong>
          </div>
        </template>

        <template #cell(nombre)="data">
          <div class="d-flex align-items-center">
            <b-avatar
              :text="data.item.nombre.charAt(0).toUpperCase()"
              variant="primary"
              class="mr-2"
            />
            <div>
              <div class="font-weight-bold">{{ data.item.nombre }}</div>
              <small class="text-muted">{{ data.item.cif }}</small>
            </div>
          </div>
        </template>

        <template #cell(tipoEmpresa)="data">
          <b-badge variant="info" class="text-capitalize">
            {{ data.item.nombreTipoEmpresa || 'Sin tipo' }}
          </b-badge>
        </template>

        <template #cell(contacto)="data">
          <div v-if="data.item.email || data.item.telefono">
            <div v-if="data.item.email" class="text-truncate">
              <feather-icon icon="MailIcon" size="14" class="mr-1" />
              {{ data.item.email }}
            </div>
            <div v-if="data.item.telefono" class="text-truncate">
              <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
              {{ data.item.telefono }}
            </div>
          </div>
          <span v-else class="text-muted">Sin contacto</span>
        </template>

        <template #cell(acciones)="data">
          <div class="d-flex">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="verDetalle(data.item)"
              class="mr-1"
            >
              <feather-icon icon="EyeIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-warning"
              size="sm"
              @click="editarEmpresa(data.item)"
              class="mr-1"
            >
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="confirmarEliminar(data.item)"
            >
              <feather-icon icon="TrashIcon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Paginación -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div class="text-muted">
          Mostrando {{ empresasFiltradas.length }} de {{ empresas.length }} empresas
        </div>
        <b-pagination
          v-model="paginaActual"
          :total-rows="empresasFiltradas.length"
          :per-page="elementosPorPagina"
          size="sm"
          class="mb-0"
        />
      </div>
    </b-card>

    <!-- Modal de confirmación de eliminación -->
    <b-modal
      id="modal-eliminar"
      title="Confirmar Eliminación"
      ok-title="Eliminar"
      cancel-title="Cancelar"
      ok-variant="danger"
      @ok="eliminarEmpresa"
    >
      <p>¿Estás seguro de que quieres eliminar la empresa <strong>{{ empresaSeleccionada?.nombre }}</strong>?</p>
      <p class="text-muted">Esta acción no se puede deshacer.</p>
    </b-modal>
  </div>
</template>

<script>
import { EmpresaService } from '@/services/empresaService'
import { TipoEmpresaService } from '@/services/tipoEmpresaService'
import { Empresa, TipoEmpresa } from '@/types'

export default {
  name: 'EmpresasLista',
  data() {
    return {
      empresas: [],
      empresasFiltradas: [],
      tiposEmpresa: [],
      cargando: false,
      paginaActual: 1,
      elementosPorPagina: 10,
      empresaSeleccionada: null,
      filtros: {
        busqueda: '',
        tipoEmpresa: null,
        tipoBusqueda: 'nombre'
      },
      empresasFields: [
        { key: 'nombre', label: 'Empresa', sortable: true },
        { key: 'tipoEmpresa', label: 'Tipo', sortable: true },
        { key: 'direccion', label: 'Dirección', sortable: true },
        { key: 'contacto', label: 'Contacto' },
        { key: 'web', label: 'Web' },
        { key: 'acciones', label: 'Acciones', thClass: 'text-center' }
      ],
      opcionesTipoBusqueda: [
        { value: 'nombre', text: 'Nombre' },
        { value: 'cif', text: 'CIF' },
        { value: 'email', text: 'Email' },
        { value: 'observaciones', text: 'Observaciones' }
      ]
    }
  },
  computed: {
    opcionesTipoEmpresa() {
      return this.tiposEmpresa.map(tipo => ({
        value: tipo.idTipoEmpresa,
        text: tipo.nombre
      }))
    }
  },
  async mounted() {
    await this.cargarDatos()
  },
  methods: {
    async cargarDatos() {
      this.cargando = true
      try {
        const [empresasData, tiposData] = await Promise.all([
          EmpresaService.getAll(),
          TipoEmpresaService.getAll()
        ])
        
        this.empresas = empresasData
        this.tiposEmpresa = tiposData
        this.filtrarEmpresas()
      } catch (error) {
        this.$toast.error('Error al cargar los datos')
        console.error('Error:', error)
      } finally {
        this.cargando = false
      }
    },
    filtrarEmpresas() {
      let filtradas = [...this.empresas]

      // Filtro por búsqueda
      if (this.filtros.busqueda) {
        const busqueda = this.filtros.busqueda.toLowerCase()
        filtradas = filtradas.filter(empresa => {
          switch (this.filtros.tipoBusqueda) {
            case 'nombre':
              return empresa.nombre.toLowerCase().includes(busqueda)
            case 'cif':
              return empresa.cif.toLowerCase().includes(busqueda)
            case 'email':
              return empresa.email?.toLowerCase().includes(busqueda)
            case 'observaciones':
              return empresa.observaciones?.toLowerCase().includes(busqueda)
            default:
              return empresa.nombre.toLowerCase().includes(busqueda)
          }
        })
      }

      // Filtro por tipo de empresa
      if (this.filtros.tipoEmpresa) {
        filtradas = filtradas.filter(empresa => 
          empresa.idTipoEmpresa === this.filtros.tipoEmpresa
        )
      }

      this.empresasFiltradas = filtradas
    },
    limpiarFiltros() {
      this.filtros = {
        busqueda: '',
        tipoEmpresa: null,
        tipoBusqueda: 'nombre'
      }
      this.filtrarEmpresas()
    },
    nuevaEmpresa() {
      this.$router.push({ name: 'empresas-nueva' })
    },
    verDetalle(empresa) {
      this.$router.push({ name: 'empresas-detalle', params: { id: empresa.idEmpresa } })
    },
    editarEmpresa(empresa) {
      this.$router.push({ name: 'empresas-editar', params: { id: empresa.idEmpresa } })
    },
    confirmarEliminar(empresa) {
      this.empresaSeleccionada = empresa
      this.$bvModal.show('modal-eliminar')
    },
    async eliminarEmpresa() {
      if (!this.empresaSeleccionada) return

      try {
        await EmpresaService.delete(this.empresaSeleccionada.idEmpresa)
        this.$toast.success('Empresa eliminada correctamente')
        await this.cargarDatos()
      } catch (error) {
        const statusCode = error?.response?.status
        const responseData = error?.response?.data
        
        // Si el backend devuelve un 409 (Conflict), es un error de integridad de datos
        if (statusCode === 409) {
          // Intentar obtener el mensaje de diferentes formas
          let errorMessage = null
          
          if (responseData) {
            if (typeof responseData === 'string') {
              errorMessage = responseData
            } else if (responseData.message) {
              errorMessage = responseData.message
            } else if (responseData.error) {
              errorMessage = responseData.error
            }
          }
          
          // Si no se encontró mensaje, usar el mensaje por defecto
          if (!errorMessage) {
            errorMessage = 'No se puede eliminar la empresa porque tiene registros asociados (obras o proyectos). Por favor, elimine primero los registros relacionados.'
          }
          
          this.$toast.error(errorMessage)
        } else if (statusCode === 404) {
          this.$toast.error('La empresa no existe o ya ha sido eliminada')
        } else if (statusCode === 500) {
          // Fallback para errores 500 (por si acaso)
          this.$toast.error('No se puede eliminar la empresa porque tiene registros asociados (obras o proyectos). Por favor, elimine primero los registros relacionados.')
        } else {
          this.$toast.error('Error al eliminar la empresa')
        }
      }
    }
  }
}
</script>

<style scoped>
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.content-header-left h1 {
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
  font-weight: 600;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.content-header-right {
  display: flex;
  gap: 0.5rem;
}

.text-truncate {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>






